// src/styles/colors.ts

export const colors = {
  // User type specific colors
  traveller: {
    primary: '#9B1B5A',
    secondary: '#4A1F6A',
    accent: '#F4CC3E',
    accentDark: '#F1BF0E',
    text: '#222222',
    textSecondary: '#6A6A6A',
    light: '#F2E6EE', 
    footerBg: '#5D1036', 
  },

  sender: {
    primary: '#7A0056',
    secondary: '#4A1F6A',
    accent: '#F4CC3E',
    accentDark: '#F1BF0E',
    text: '#222222',
    textSecondary: '#6A6A6A',
    light: '#F2E6EE',
    footerBg: '#7A0056',
  },

  receiver: {
    primary: '#7A0056',
    background: '#F5EEF2',
    text: '#181D27',
    textSecondary: '#535862',
  },

  // Avatar badge colors used across group chat (sender/traveler/receiver participants)
  chatAvatar: {
    sender: {
      background: '#D6B0CB',
      text: '#7A0056',
    },
    traveller: {
      background: '#B5D2F9',
      text: '#0097D3',
    },
    receiver: {
      background: '#DBE9FC',
      text: '#0C53B0',
    },
  },

  generic: {
    primary: '#9B1B5A',
    secondary: '#4A1F6A',
    accent: '#F4CC3E',
    accentDark: '#F1BF0E',
    text: '#222222',
    textSecondary: '#6A6A6A',
    footerBg: '#370027',
    testimonialGradient: 'linear-gradient(213.1deg, #B1007E -7.9%, #7A0056 31.48%, #0B1A33 129.67%)',
  },

  // Neutral colors
  neutral: {
    white: '#FFFFFF',
    gray50: '#FAFAFA',
    gray100: '#F5F5F5',
    gray200: '#E5E5E5',
    gray300: '#D4D4D4',
    gray400: '#A3A3A3',
    gray500: '#737373',
    gray600: '#525252',
    gray700: '#404040',
    gray800: '#262626',
    gray900: '#171717',
    black: '#000000',
  },

  // Form colors
  form: {
    label: '#222222',
    input: '#6A6A6A',
    placeholder: '#777B8B',
    border: '#C6C8D0',
    borderLight: '#DDDDDD',
    background: '#FFFFFF',
  },

  // Button colors
  button: {
    primary: '#1F2937',
    primaryHover: '#111827',
    disabled: '#777B8B',
  },

  // Glass morphism backgrounds
  glass: {
    white15: 'rgba(255, 255, 255, 0.20)',
    white30: 'rgba(255, 255, 255, 0.30)',
    white95: 'rgba(255, 255, 255, 0.95)',
    white10: 'rgba(255, 255, 255, 0.10)',
  },

  // Semantic colors
  semantic: {
    success: '#1CA34E',
    error: '#D12A2A',
    warning: '#F59E0B',
    info: '#3B82F6',
    alert: '#777B8B',
  },

  kycBanner: {
    background: '#FEFAEC',
    border: '#E8AD00',
    text: '#5D4037',
    button: '#A57B00',
    buttonHover: '#e8ae00e3',
    icon: '#E8AD00',
  },

  payoutBanner: {
    titleColor: '#414651',
    bodyColor: '#535862',
    closeColor: '#A4A7AE',

    warning: {
      background: '#FDF7E6',
      border: '#E8AD00',
      icon: '#DC6803',
      button: '#A57B00',
      buttonHover: '#8A6500',
    },

    blue: {
      background: '#EEF4FF',
      border: '#C7D7FE',
      icon: '#3538CD',
      button: '#3538CD',
      buttonHover: '#2B2EA8',
    },

    orange: {
      background: '#FEF6EE',
      border: '#F9DBAF',
      icon: '#DC6803',
      button: '#DC6803',
      buttonHover: '#B85502',
    },

    success: {
      background: '#E9F7EE',
      border: '#1FB356',
      icon: '#1FB356',
      button: '#1FB356',
      buttonHover: '#178F47',
    },

    error: {
      background: '#FDEAEA',
      border: '#E62E2E',
      icon: '#E62E2E',
      button: '#E62E2E',
      buttonHover: '#C42222',
    },

    processing: {
      background: '#F4F3FF',
      border: '#5925DC',
      icon: '#5925DC',
      button: '#5925DC',
      buttonHover: '#4A1FBF',
    },

    pink: {
      background: '#F5E8EF',
      border: '#7A0056',
      icon: '#7A0056',
      button: '#7A0056',
      buttonHover: '#5C0040',
    },
  },

  // Claim status colors
  claim: {
    text: '#5925DC',
    background: '#D9D6FE',
  },

  // Action button colors for notifications and dialogs
  action: {
    accept: '#1CA34E',
    reject: '#D12A2A',
  },

  // Payment history table colors
  paymentHistory: {
    tableHeaderBg: '#FAFAFA',
    tableBorder: '#E9EAEB',
    tableText: '#535862',
    tableHeaderText: '#717680',
    amountPositive: '#079455',
    amountNegative: '#E62E2E',
    statusCompleted: {
      bg: '#ECFDF3',
      border: '#ABEFC6',
      text: '#067647',
    },
    statusPending: {
      bg: '#FEF6EE',
      border: '#F9DBAF',
      text: '#B93815',
    },
    statusRefund: {
      bg: '#EEF4FF',
      border: '#C7D7FE',
      text: '#3538CD',
    },
  },

  // Page / surface background colors
  background: {
    default: '#FAFAFA',
    paper: '#F5F5F5',
  },

  // Tooltip background
  tooltip: '#36091F',

  // Overlay / backdrop colors (used by DialogWrapper)
  overlay: {
    backdrop: 'rgba(0, 0, 0, 0.5)',
    backdropLight: 'rgba(0, 0, 0, 0.3)',
    backdropDark: 'rgba(0, 0, 0, 0.7)',
  },

  // Footer specific colors
  footer: {
    background: '#5D1036',
    text: '#FFFFFF',
    divider: 'rgba(255, 255, 255, 0.3)',
    iconBorder: 'rgba(255, 255, 255, 0.3)',
    iconHover: 'rgba(255, 255, 255, 0.1)',
  },

  // Card colors
  card: {
    background: '#FFFFFF',
    border: '#E9EAEB',
  },

  // Text colors
  text: {
    primary: '#181D27',
    secondary: '#535862',
    disabled: '#9B9CA6',
  },

  // Border colors
  border: {
    default: '#E9EAEB',
    light: '#F2F4F7',
    strong: '#ACAFBB',
  },

  // Order/delivery status colors
  status: {
    pending: {
      bg: '#FEF6EE',
      border: '#F9DBAF',
      text: '#B93815',
    },
    confirmed: {
      bg: '#EEF4FF',
      border: '#C7D7FE',
      text: '#3538CD',
    },
    completed: {
      bg: '#ECFDF3',
      border: '#ABEFC6',
      text: '#067647',
    },
    cancelled: {
      bg: '#FFF0F0',
      border: '#FECDCA',
      text: '#991B1B',
    },
    inProgress: {
      bg: '#EEF4FF',
      border: '#C7D7FE',
      text: '#1E40AF',
    },
    delivered: {
      bg: '#ECFDF3',
      border: '#ABEFC6',
      text: '#067647',
    },
    underReview: {
      bg: '#FFF1C2',
      border: '#BF6A02',
      text: '#BF6A02',
    },
    claimUnderReview: {
      bg: '#F4F3FF',
      border: '#D9D6FE',
      text: '#5925DC',
    },
    closed: {
      bg: '#FDF2FA',
      border: '#C11574',
      text: '#C11574',
    },
  },
} as const;

// Gradients with exact specifications from design
export const gradients = {
    hero: 'linear-gradient(102.36deg, #9B1B5A -0.38%, #4A1F6A 191.36%)',
    // Logo gradient
    logo: 'linear-gradient(225deg, #F1BF0E 49%, rgba(241, 191, 14, 0) 100%)',
} as const;

// Shadow definitions matching design specs
export const shadows = {
  // Search form shadow from JSX
  searchForm: '0px 3px 12px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.08)',
  // Navbar shadow on scroll
  navbar: '0 4px 12px rgba(0, 0, 0, 0.1)',
  AuthNavbar: '0 4px 12px rgba(0, 0, 0, 0.1)',
  // Card shadows
  card: '0 2px 8px rgba(0, 0, 0, 0.1)',
  cardHover: '0 4px 16px rgba(0, 0, 0, 0.15)',
  // Dialog shadows
  xs: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  xl: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
} as const;

// Type exports
export type Colors = typeof colors;
export type Gradients = typeof gradients;
export type Shadows = typeof shadows;